import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { clearAllLastOrgIdHints } from '@finvox/shared/storage';
import { getFirebaseAuth, resetFirestoreSession } from '@/lib/firebase';

const AUTH_WAIT_MS = 10_000;

/** Wait until Firebase auth matches expected uid (null = signed out). */
export function waitForAuthUser(expectedUid: string | null, timeoutMs = AUTH_WAIT_MS): Promise<User | null> {
  const auth = getFirebaseAuth();
  if (expectedUid === null && !auth.currentUser) return Promise.resolve(null);
  if (expectedUid && auth.currentUser?.uid === expectedUid) return Promise.resolve(auth.currentUser);

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsub();
      reject(new Error('Auth state timeout'));
    }, timeoutMs);

    const unsub = onAuthStateChanged(auth, (u) => {
      if (expectedUid === null && !u) {
        clearTimeout(timer);
        unsub();
        resolve(null);
        return;
      }
      if (expectedUid && u?.uid === expectedUid) {
        clearTimeout(timer);
        unsub();
        resolve(u);
      }
    });
  });
}

/** Full teardown: clear org hints, sign out Firebase, hard-navigate to login. */
export async function resetSessionAndGoToAuth(): Promise<never> {
  clearAllLastOrgIdHints();
  try {
    await signOut(getFirebaseAuth());
  } catch {
    /* still navigate */
  }
  window.location.assign('/auth');
  return new Promise(() => {}) as never;
}

/** Clear cached Firestore docs before loading the next signed-in account. */
export async function prepareFreshFirestoreSession(): Promise<void> {
  await resetFirestoreSession();
}

/** Hard reload app shell after sign-in so Firestore listeners/cache cannot bleed across accounts. */
export async function completeSignInNavigation(): Promise<never> {
  await prepareFreshFirestoreSession();
  window.location.assign('/');
  return new Promise(() => {}) as never;
}
